import React, { useEffect, useState, useCallback } from "react";
import { Mail, Trash2, CheckCircle2, RefreshCw, Inbox } from "lucide-react";
import { api } from "../lib/api";

const SUBJECT_LABELS = {
  general: "Generale",
  deal: "Segnalazione offerta",
  partnership: "Collaborazione",
  bug: "Bug / Errore",
  advertising: "Pubblicità",
  privacy: "Privacy / GDPR",
};

export default function MessagesTab() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/contact-messages");
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get("/admin/contact-messages");
        if (!active) return;
        setItems(r.data.items || []);
        setUnread(r.data.unread || 0);
      } catch (e) {
        console.error(e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const markRead = async (id) => {
    try {
      await api.put(`/admin/contact-messages/${id}/read`);
      load();
    } catch (e) {
      alert("Errore: " + (e?.response?.data?.detail || e.message));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo messaggio?")) return;
    try {
      await api.delete(`/admin/contact-messages/${id}`);
      if (selectedId === id) setSelectedId(null);
      load();
    } catch (e) {
      alert("Errore: " + (e?.response?.data?.detail || e.message));
    }
  };

  const selected = items.find((m) => m.id === selectedId);

  return (
    <div data-testid="messages-tab">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-black text-2xl text-white">
            Messaggi dal sito
          </h1>
          <p className="text-zinc-500 text-sm">
            Messaggi ricevuti dal modulo Contatti.{" "}
            {unread > 0 && (
              <span className="text-[#FBD51C] font-bold">
                {unread} da leggere
              </span>
            )}
          </p>
        </div>
        <button
          onClick={load}
          data-testid="messages-refresh"
          className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800 text-sm font-bold px-4 py-2 rounded-lg"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aggiorna
        </button>
      </div>

      {items.length === 0 && !loading && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-10 text-center">
          <Inbox size={32} className="mx-auto text-zinc-600 mb-3" />
          <div className="text-zinc-400">Nessun messaggio ricevuto</div>
          <div className="text-xs text-zinc-600 mt-1">
            Quando un utente compilerà il modulo Contatti apparirà qui.
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* list */}
          <div className="md:col-span-1 space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {items.map((m) => (
              <button
                key={m.id}
                onClick={() => {
                  setSelectedId(m.id);
                  if (!m.read) markRead(m.id);
                }}
                data-testid={`message-item-${m.id}`}
                className={`w-full text-left bg-zinc-900 border rounded-xl p-3 hover:border-[#FBD51C]/50 transition ${
                  selectedId === m.id
                    ? "border-[#FBD51C]/60"
                    : "border-zinc-800"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {!m.read && (
                    <span className="w-2 h-2 rounded-full bg-[#FBD51C] shrink-0" />
                  )}
                  <span className="font-bold text-white text-sm line-clamp-1">
                    {m.name}
                  </span>
                </div>
                <div className="text-xs text-zinc-400 line-clamp-1">{m.email}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] font-mono-bdd uppercase tracking-wider text-zinc-500">
                    {SUBJECT_LABELS[m.subject] || m.subject}
                  </span>
                  <span className="text-[10px] text-zinc-600">
                    {new Date(m.created_at).toLocaleDateString("it-IT")}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* detail */}
          <div className="md:col-span-2">
            {selected ? (
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                  <div>
                    <div className="text-xs font-mono-bdd uppercase tracking-wider text-[#FBD51C] mb-1">
                      {SUBJECT_LABELS[selected.subject] || selected.subject}
                    </div>
                    <h2 className="font-display text-xl text-white">
                      {selected.name}
                    </h2>
                    <a
                      href={`mailto:${selected.email}?subject=Re: ${
                        SUBJECT_LABELS[selected.subject] || ""
                      }`}
                      className="text-sm text-zinc-400 hover:text-[#FBD51C] inline-flex items-center gap-1.5 mt-1"
                    >
                      <Mail size={12} /> {selected.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    {selected.read ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono-bdd uppercase tracking-wider text-emerald-400">
                        <CheckCircle2 size={11} /> Letto
                      </span>
                    ) : null}
                    <button
                      onClick={() => remove(selected.id)}
                      data-testid="message-delete"
                      className="text-zinc-400 hover:text-rose-400 p-2"
                      title="Elimina"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="text-xs text-zinc-500 mb-3">
                  Ricevuto il{" "}
                  {new Date(selected.created_at).toLocaleString("it-IT")}
                </div>

                <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 text-zinc-300 whitespace-pre-wrap text-sm leading-relaxed">
                  {selected.message}
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <a
                    href={`mailto:${selected.email}?subject=Re: ${
                      SUBJECT_LABELS[selected.subject] || "Messaggio"
                    }&body=Ciao ${selected.name},%0D%0A%0D%0A`}
                    data-testid="message-reply"
                    className="inline-flex items-center gap-2 bg-[#FBD51C] hover:bg-[#FFE552] text-black font-bold text-sm px-4 py-2 rounded-lg"
                  >
                    <Mail size={14} /> Rispondi via email
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-10 text-center text-zinc-500">
                Seleziona un messaggio per leggerlo
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
